import * as React from "react";
import { Link } from "@tanstack/react-router";

import type { SpecEditSearch } from "@/api/contracts/specifications";
import type { IslandSearchPatch } from "@/lib/item-search-island";
import { BreadcrumbItem, BreadcrumbLink, Breadcrumbs } from "@/components/ui/breadcrumbs";
import { EmptyTable } from "@/components/ui/empty-table";
import { FullPageError } from "@/components/ui/full-page-error";
import { Loader } from "@/components/ui/loader";
import { MuiTypography } from "@/components/ui/mui-typography";
import { usePermissions } from "@/hooks/permissions";
import { useMoveSpecLine } from "@/hooks/spec-editor-lines";
import { useSpecEditorTarget, type SpecEditorTarget } from "@/hooks/spec-editor-target";
import {
  useSpecGroupExpansion,
  useSpecLinesLoader,
  useSpecScrollRegion,
  type SpecLinesLoader,
} from "@/hooks/spec-lines";
import { editorSwitchSetting, useTargetCostSwitch } from "@/hooks/target-cost-switch";
import type { SpecLinesGroup, SpecLinesLine, SpecLinesSelection } from "@/hooks/specifications";
import { ERRORS } from "@/lib/spec-editor-errors";
import { EDIT_BREADCRUMB_LEAF, breadcrumbRoot, editDisplayName } from "@/lib/spec-display";
import {
  editorRows,
  offersChangesSwitch,
  resolveActiveRow,
  targetCostSwitchLabel,
} from "@/lib/spec-lines-display";
import { specDetailPath, specListPath } from "@/lib/route-paths";

import type { SpecHeaderEditFields } from "./edit-spec-name-dialog";
import { EditSpecSummary } from "./edit-spec-summary";
import { ItemSearchIsland, type IslandItemDialog } from "./item-search-island";
import { EditableSpecGroups, type SpecLineMove } from "./editable-spec-groups";
import type { SpecDetailsLoaded, SpecDetailsSummary } from "./spec-details-frame";
import { SpecEditFrame, SpecEditIslandSkeleton, SpecEditSkeletonPair } from "./spec-edit-frame";
import { PerPlanManufacturerDialog } from "./per-plan-manufacturer-dialog";
import { AddSpecGroup } from "./spec-group-dialogs";
import {
  AddSpecLineDialog,
  DeleteSpecLineDialog,
  DuplicateSpecLineDialog,
  EditSpecLineDialog,
  type SpecActiveRow,
} from "./spec-line-dialogs";
import { SpecLineLocationNotes } from "./spec-line-location-notes";
import { SpecLineLocations } from "./spec-line-locations";
import {
  SpecLineMenu,
  type SpecLineDialogRequest,
  type SpecLineMenuDialog,
} from "./spec-line-menu";
import { DeleteSpecSelectionDialog, EditSpecSelectionDialog } from "./spec-selection-dialogs";
import { SpecEditorLine, type SpecEditorLineSlots } from "./spec-editor-line";
import { SpecLinesToolbar } from "./spec-lines-toolbar";
import { SpecificationNotes } from "./specification-notes";
import { TargetCostNotification } from "./target-cost-notification";

// The edit page — the donor's `SpecEditIsland` (`SpecEditIsland/
// SpecEditIsland.tsx`) in its frame (`pages/spec-edit-page.tsx`) (#806), over
// the pieces the chain built: #799's header write, #805's editable groups and
// group controls, and the Read side's lines loader, expansion map, toolbar,
// notes block and read-only rows.
//
// **The header stack** (`Header/Container.tsx`): a white column, gap 12, z 9,
// `pt 26`, not sticky — the breadcrumbs; the name and its pencil, then the
// notes; the search row with `Add group`; the controls row.
//
// **The body**, beneath it in the island's order (`:200-316`): the groups up to
// the display cursor, the search skeleton while a new term loads, then at most
// one face —
//
//   1. **a failed lines read** (the first page or a later one): the shared
//      `FullPageError` beneath what loaded, `ERRORS.LOADER` and a `Refresh
//      Page` that retries the failed read. The donor showed `Get Started` under
//      a snackbar that never went away; owner-ruled (D1a / D14, structure
//      rulings item 9);
//   2. **a truly empty version**: `Get Started` (the loader's `isZeroState`);
//   3. **any other empty match**: `No results` (D1b / D13);
//
// and the pagination skeleton while lines remain.
//
// **Line rows** are the Editor's own (#809, `SpecEditorLine`), drawn from one
// place (`renderLine`): the six-cell rows with the status pill and the lock,
// struck rows under "Show changes from Base", and a line drag within its group
// through #800's move verb. Their item click opens the read-only item dialog
// until #811 gives it the island's CTA mode. **`Add specification line`**
// opens that group's `Add Specification Line` (#807) — one dialog per group,
// as the donor renders one `AddLineToGroup` per card, mounted at its first
// opening and kept, so each group's dialog keeps its own emptied-name hold and
// its own held row.
//
// **The Active selection** (`CONTEXT.md`) is this page's state: the line and
// selection a row click, the pill, the lock or a new line set, by id. It is
// read through one resolver over the loaded lines, so a pair whose row has
// left the cache reads as none, and nothing but a set changes it — a search
// or a page landing keeps it, as the donor's slice does. #811's island reads
// it next.
//
// **Slots #810 fills on every row** (memoized, so a row re-renders only when
// the rooms or its own facts move): the ⋮ (`SpecLineMenu`), the locations
// select under a line-level row's name, the per-location notes table under a
// `specific` line, and the per-plan link's manufacturer dialog.
//
// **One dialog host** for what those open — the ⋮'s five (#807) and the
// manufacturer dialog (#858). The donor mounted each in its row; hosted here,
// the rows stay free of the page facts the dialogs read (the version's
// groups, the scope). Three donor facts the move must keep by hand:
//
//   - **every click inside a hosted dialog is a click on its row** — the
//     donor's dialogs were the row's React children, so `Cancel` on a delete
//     confirm left the row active (measured, #810 enumeration 22). Escape is a
//     key and moves nothing;
//   - **a hosted dialog lives only while its row is drawn** — the donor's
//     row left the list in the tick a landed delete closed its confirm
//     (`DeleteDialog.tsx:36-44,58-64`), unmounting the confirm with no fade,
//     and any write that stops drawing a row takes its open dialog with it.
//     A confirm whose row stays, struck in place under "Show changes", fades
//     as any close does;
//   - **the manufacturer dialog keeps its draft across a close** (#858), as
//     the donor's, always mounted in its row, does — so it is mounted once per
//     row at its first opening and kept, as `Add specification line` is per
//     group. The ⋮'s five mount only while open (#807 D1).
//
// **No page-level snackbar**: each control carries its own (#799, #805), and
// `ERRORS.LOADER` is reached only as the failed-read face's message (#806
// Assumption 13). The one page-level toast is target cost's (below).
//
// **Target cost** (#1216): the switch's setting follows the spec's type, by
// the rule its label follows (`editorSwitchSetting`): a community spec's
// "Show Target Cost" is the visit's one target-cost setting, the details
// page's too, and a base or core spec's "Show Max Price" is its own (#1241
// D1). Both are session-wide and on by default. The switch is offered to every
// specs editor on every type; it drives the Item Search
// cards' Max Price chips, handed to the island as a prop (PB-T7); and, on a
// community spec, the toast, mounted past the island skeleton while it is on
// (`SpecEditIsland.tsx:142-147`). The toast's count is the live summary's,
// which every Editor result rewrites (ADR-0014 decision 6), so it is fresh
// whenever it next mounts (PB-T6) — and a select never mounts it (Q18).

interface SpecEditPageProps {
  /** The summary and the draft on it, found by the route. */
  read: SpecDetailsLoaded;
  search: SpecEditSearch;
  /** Writes `?specSearch=`; `""` removes it. */
  onSearchChange: (term: string) => void;
  /** Merges the Item Search island's keys into the URL, replacing the entry. */
  onIslandSearchChange: (patch: IslandSearchPatch) => void;
}

/**
 * The route's pending face — the donor's `<Loading />` while the spec is read
 * (`specification-edit-route.tsx:35`, `components/loading/loading.tsx`): the
 * page-sized Lottie loader, centred in a viewport-tall box.
 */
export function SpecEditPending() {
  return (
    <div data-testid="spec-edit-pending" className="h-screen">
      <Loader size="page" />
    </div>
  );
}

export function SpecEditPage({
  read,
  search,
  onSearchChange,
  onIslandSearchChange,
}: SpecEditPageProps) {
  const { summary, version } = read;
  const permissions = usePermissions();
  const urlSearch = search.specSearch ?? "";
  const loader = useSpecLinesLoader(summary.id, version.id, urlSearch, { tellsZeroState: true });
  const expansion = useSpecGroupExpansion(loader.ordering?.map((entry) => entry.group.id));
  const target = useSpecEditorTarget(summary.id, version.id, urlSearch);
  // "Show changes from Base": the island's state, off on arrival, never kept.
  const [showChanges, setShowChanges] = React.useState(false);
  const [showTargetCost, setShowTargetCost] = useTargetCostSwitch(editorSwitchSetting(summary));
  // The one item dialog (#811): from a row's item, or a card in the island.
  const [itemDialog, setItemDialog] = React.useState<IslandItemDialog | null>(null);
  const onOpenItem = React.useCallback(
    (seed: IslandItemDialog["seed"], row: SpecActiveRow) => setItemDialog({ seed, target: row }),
    [],
  );

  // `Add specification line` (#807): the groups whose dialog has been opened,
  // and the one open now. A group a search page does not draw keeps its last
  // object, so its dialog — and what it holds — survives the search.
  const [addLineGroups, setAddLineGroups] = React.useState<ReadonlyMap<number, SpecLinesGroup>>(
    () => new Map(),
  );
  const [addLineOpen, setAddLineOpen] = React.useState<number | null>(null);
  const onAddLine = React.useCallback((group: SpecLinesGroup) => {
    setAddLineGroups((current) => new Map(current).set(group.id, group));
    setAddLineOpen(group.id);
  }, []);
  const [activeRow, setActiveRow] = React.useState<SpecActiveRow | null>(null);
  const [hosted, setHosted] = React.useState<HostedDialogs>({});
  const openDialog = React.useCallback(({ dialog, line, selection }: SpecLineDialogRequest) => {
    setHosted((now) => ({ ...now, [dialog]: { open: true, row: { line, selection } } }));
  }, []);
  const [manufacturerRows, setManufacturerRows] = React.useState<
    ReadonlyMap<string, ManufacturerRow>
  >(() => new Map());
  const [manufacturerOpen, setManufacturerOpen] = React.useState<string | null>(null);
  const onOpenManufacturer = React.useCallback(
    (
      line: SpecLinesLine,
      selection: SpecLinesSelection | undefined,
      kind: ManufacturerRow["kind"],
    ) => {
      const key = manufacturerKey(line, selection, kind);
      setManufacturerRows((now) => new Map(now).set(key, { line, selection, kind }));
      setManufacturerOpen(key);
    },
    [],
  );
  const specType = summary.type;
  const slots = React.useMemo<SpecEditorLineSlots>(
    () => ({
      menu: (facts) => (
        <SpecLineMenu {...facts} specType={specType} target={target} onOpenDialog={openDialog} />
      ),
      locations: (line) => <SpecLineLocations line={line} rooms={loader.rooms} target={target} />,
      locationNotes: (line, facts) => (
        <SpecLineLocationNotes line={line} target={target} {...facts} />
      ),
      onOpenManufacturer,
    }),
    [specType, target, openDialog, loader.rooms, onOpenManufacturer],
  );
  const { mutateAsync: moveLine } = useMoveSpecLine();
  const currentGroups = React.useMemo(
    () => new Map(loader.ordering?.map((entry) => [entry.group.id, entry.group])),
    [loader.ordering],
  );
  // The Active selection as the loaded lines hold it: the pair itself while
  // its line and selection are cached, else none — the one place it resolves.
  const active = React.useMemo(
    () =>
      resolveActiveRow(loader.ordering?.flatMap((entry) => entry.lines) ?? [], activeRow)
        ? activeRow
        : null,
    [loader.ordering, activeRow],
  );
  const onMoveLine = React.useCallback(
    ({ lineId, afterId }: SpecLineMove) => moveLine({ ...target(), lineId, afterId }),
    [moveLine, target],
  );
  // The one place the Editor's rows are chosen (#806's renderer site).
  const renderLine = (line: SpecLinesLine) => (
    <SpecEditorLine
      line={line}
      specType={summary.type}
      showChanges={showChanges}
      // Only the line the pair is on sees it, so a click re-renders two lines.
      active={active?.lineId === line.id ? active : null}
      onActiveChange={setActiveRow}
      onOpenItem={onOpenItem}
      target={target}
      slots={slots}
    />
  );
  const lines = loader.ordering?.flatMap((entry) => entry.lines);
  // A hosted dialog reads its row as the cache holds it now, else as it opened.
  const current = ({ line, selection }: HostedRow): HostedRow => {
    const held = lines?.find((candidate) => candidate.id === line.id) ?? line;
    return {
      line: held,
      selection: selection && (held.selections.find(({ id }) => id === selection.id) ?? selection),
    };
  };
  const closeHosted = (dialog: SpecLineMenuDialog) => (open: boolean) => {
    if (open) return;
    setHosted((now) => {
      const held = now[dialog];
      return held ? { ...now, [dialog]: { ...held, open: false } } : now;
    });
  };
  // The donor's row heard every click its dialogs took (see the module comment).
  const rowClick = (row: HostedRow) => () =>
    setActiveRow({ lineId: row.line.id, selectionId: row.selection?.id ?? null });
  const clearActive = () => setActiveRow(null);
  // Whether the row a hosted dialog was opened on is still drawn — the
  // donor's dialog was that row's child, mounted only while the row was. A
  // line-level row for a line's dialogs; that selection's own row for a
  // selection's, which goes when the line collapses to one shown selection
  // (`EditableLine.tsx:52-55,76-78`). So a landed delete's confirm leaves with
  // its row, with no fade, unless the row stays drawn — struck in place under
  // "Show changes from Base" (`EditableLine.tsx:62-64`, `LineContentRow.tsx:
  // 84-91`) — where it closes as any dialog does. And the row is the one it
  // opened on only in the same group, where the donor drew a line as its
  // group's child (`SpecEditIsland.tsx:209-229`) and remounted it in another
  // on a move, and only while unfinalized, as the ⋮ cell is the lock on a
  // finalized line (`LineContentRow.tsx:393-401`) — a failed unlock rolls it
  // back with a dialog open.
  const stillDrawn = (dialog: SpecLineMenuDialog, { line, selection }: HostedRow) => {
    const held = lines?.find((candidate) => candidate.id === line.id);
    if (!held || held.groupId !== line.groupId || held.status === "finalized") return false;
    const onSelection = dialog === "edit-selection" || dialog === "delete-selection";
    return editorRows(held, showChanges).some((row) =>
      onSelection
        ? row.kind === "SELECTION_ONLY" && row.selection.id === selection?.id
        : row.kind === "LINE_ONLY" || row.kind === "LINE_AND_SELECTION",
    );
  };
  // The manufacturer dialog's row, by the same rule: the donor mounts it in
  // the per-plan summary (`LinePerPlan`), drawn on a row carrying the
  // selection while its line is per plan — and on a finalized line too, whose
  // grey link still opens it.
  // On the row kind it opened on, as the donor's single row and its selection
  // rows under a header are different rows (`EditableLine.tsx:68-78`).
  const manufacturerDrawn = ({ line, selection, kind }: ManufacturerRow) => {
    const held = lines?.find((candidate) => candidate.id === line.id);
    if (!held || held.groupId !== line.groupId || !held.perPlan) return false;
    return editorRows(held, showChanges).some(
      (row) => row.kind === kind && row.selection?.id === selection?.id,
    );
  };
  // A dialog that left with its row is gone, as the donor's unmounted one
  // was: the entry goes too, so the row drawn again opens nothing, and a
  // manufacturer draft starts over as the donor's remounted form did. Set
  // during render, guarded, so the commit that stops drawing the row is the
  // one that forgets its dialog.
  const goneDialogs = (Object.keys(hosted) as SpecLineMenuDialog[]).filter(
    (dialog) => !stillDrawn(dialog, hosted[dialog]!.row),
  );
  if (goneDialogs.length > 0) {
    const next = { ...hosted };
    for (const dialog of goneDialogs) delete next[dialog];
    setHosted(next);
  }
  const goneRows = [...manufacturerRows]
    .filter(([, opened]) => !manufacturerDrawn(opened))
    .map(([key]) => key);
  if (goneRows.length > 0) {
    const next = new Map(manufacturerRows);
    for (const key of goneRows) next.delete(key);
    setManufacturerRows(next);
    if (manufacturerOpen !== null && goneRows.includes(manufacturerOpen)) {
      setManufacturerOpen(null);
    }
  }

  // The right pane (#811), the same element in both returns below, so the
  // island keeps its state when the editor's first page lands — each return
  // is a fragment led by the frame.
  const island = (
    <ItemSearchIsland
      lines={lines ?? []}
      activeRow={activeRow}
      onActiveRowChange={setActiveRow}
      search={{
        itemSearch: search.itemSearch,
        category: search.category,
        mfrs: search.mfrs,
        tiers: search.tiers,
        status: search.status,
      }}
      onSearchChange={onIslandSearchChange}
      target={target}
      itemDialog={itemDialog}
      onItemDialogChange={setItemDialog}
      showMaxPrice={showTargetCost}
    />
  );

  if (loader.isInitialLoading) {
    // Rooted in a fragment, as the landed face is: React keeps a subtree only
    // while each element on its path keeps its type, so the frame, and the
    // island in it, survive the editor's first load with their search.
    return (
      <>
        <SpecEditFrame rightPane={island}>
          <SpecEditIslandSkeleton />
        </SpecEditFrame>
      </>
    );
  }

  return (
    <>
      <SpecEditFrame rightPane={island}>
        <div
          data-testid="spec-edit-header"
          className="relative z-[9] flex w-full flex-col gap-3 bg-white pt-[26px]"
        >
          <Breadcrumbs data-testid="breadcrumbs">
            <BreadcrumbLink>
              <Link to={specListPath(summary.type)}>{breadcrumbRoot(summary)}</Link>
            </BreadcrumbLink>
            <BreadcrumbLink>
              <Link to={specDetailPath(summary.id, version.id)}>{summary.name}</Link>
            </BreadcrumbLink>
            <BreadcrumbItem>{EDIT_BREADCRUMB_LEAF}</BreadcrumbItem>
          </Breadcrumbs>
          <div>
            {/* `SpecDisplayName.tsx:32-44`: a wrapping row, gap 8, a column
                below 600px; inside it the name and the pencil, 4px apart. */}
            <div className="flex flex-col flex-wrap justify-between gap-2 min-[600px]:flex-row min-[600px]:items-center">
              <div className="flex flex-row items-center gap-1">
                <MuiTypography variant="h2" component="h2" data-testid="spec-name">
                  {editDisplayName(summary)}
                </MuiTypography>
                <EditSpecSummary target={target} spec={headerEditFields(summary)} />
              </div>
            </div>
            <SpecificationNotes notes={summary.notes} />
          </div>
          <SpecLinesToolbar
            urlSearch={urlSearch}
            onSearch={onSearchChange}
            isSearching={loader.isSearching}
            isEmpty={loader.isEmpty}
            expansion={expansion}
            showChangesSwitch={offersChangesSwitch(summary)}
            showChanges={showChanges}
            onShowChangesChange={setShowChanges}
            // Every specs editor, every type (`SpecEditIsland.tsx:189`).
            showTargetCostSwitch={permissions.canEditSpecs}
            showTargetCost={showTargetCost}
            onShowTargetCostChange={setShowTargetCost}
            targetCostLabel={targetCostSwitchLabel(summary)}
            searchAction={<AddSpecGroup target={target} />}
          />
        </div>

        <EditableSpecGroups
          loader={loader}
          expansion={expansion}
          target={target}
          onAddLine={onAddLine}
          renderLine={renderLine}
          onMoveLine={onMoveLine}
          specType={summary.type}
          showChanges={showChanges}
        />

        {loader.isSearching && !loader.isRetrying ? (
          <SpecEditSkeletonPair testId="spec-edit-search-skeleton" />
        ) : null}

        <SpecEditFace loader={loader} target={target} />

        {loader.showPaginationSkeleton ? (
          <EditPaginationSkeleton onLoadMore={loader.loadMore} />
        ) : null}
      </SpecEditFrame>
      {[...addLineGroups].map(([groupId, opened]) => (
        <AddSpecLineDialog
          key={groupId}
          group={currentGroups.get(groupId) ?? opened}
          target={target}
          open={addLineOpen === groupId}
          onOpenChange={(open) => setAddLineOpen(open ? groupId : null)}
          onCreated={setActiveRow}
        />
      ))}
      {(Object.keys(hosted) as SpecLineMenuDialog[]).map((dialog) => {
        const held = hosted[dialog]!;
        if (!stillDrawn(dialog, held.row)) return null;
        const row = current(held.row);
        const common = {
          line: row.line,
          target,
          open: held.open,
          onOpenChange: closeHosted(dialog),
        };
        return (
          <div key={dialog} className="contents" onClick={rowClick(row)}>
            {dialog === "edit-line" ? (
              <EditSpecLineDialog {...common} groups={loader.versionGroups} />
            ) : dialog === "duplicate-line" ? (
              <DuplicateSpecLineDialog {...common} groups={loader.versionGroups} />
            ) : dialog === "delete-line" ? (
              <DeleteSpecLineDialog {...common} onDeleted={clearActive} />
            ) : row.selection === undefined ? null : dialog === "edit-selection" ? (
              <EditSpecSelectionDialog {...common} selection={row.selection} />
            ) : (
              <DeleteSpecSelectionDialog
                {...common}
                selection={row.selection}
                onDeleted={clearActive}
              />
            )}
          </div>
        );
      })}
      {[...manufacturerRows].map(([key, opened]) => {
        const row = current(opened);
        if (!row.selection || !manufacturerDrawn(opened)) return null;
        return (
          <div key={key} className="contents" onClick={rowClick(row)}>
            <PerPlanManufacturerDialog
              open={manufacturerOpen === key}
              specificationId={summary.id}
              versionId={version.id}
              scope={target().scope}
              line={row.line}
              selection={row.selection}
              manufacturer={row.selection.manufacturer ?? null}
              onClose={() => setManufacturerOpen(null)}
            />
          </div>
        );
      })}
      {summary.type === "community" && permissions.canEditSpecs && showTargetCost ? (
        <TargetCostNotification count={summary.overTargetCount} />
      ) : null}
    </>
  );
}

/** The row a hosted dialog was opened on. */
interface HostedRow {
  line: SpecLinesLine;
  selection: SpecLinesSelection | undefined;
}

/** The ⋮'s dialogs, each held from its first opening for its exit — while its
 *  row is drawn. */
type HostedDialogs = Partial<Record<SpecLineMenuDialog, { open: boolean; row: HostedRow }>>;

/** A manufacturer dialog's row: the hosted row, and which kind of row it is. */
interface ManufacturerRow extends HostedRow {
  kind: "LINE_AND_SELECTION" | "SELECTION_ONLY";
}

/** One manufacturer dialog per per-plan row: its kind, and its selection or line. */
function manufacturerKey(
  line: SpecLinesLine,
  selection: SpecLinesSelection | undefined,
  kind: ManufacturerRow["kind"],
) {
  return `${kind}-${selection ? `selection-${selection.id}` : `line-${line.id}`}`;
}

/** The one face beneath the groups, if any — see the module comment. */
function SpecEditFace({ loader, target }: { loader: SpecLinesLoader; target: SpecEditorTarget }) {
  if (loader.isError) {
    return (
      <FullPageError message={ERRORS.LOADER} onRetry={loader.retry} isLoading={loader.isRetrying} />
    );
  }
  if (loader.isZeroState) return <ZeroState target={target} />;
  if (loader.isEmptyMatch) return <EmptyTable text="No results" className="mr-8" />;
  return null;
}

/**
 * `ZeroState` (`components/ZeroState.tsx`) in its `pr 32` cell
 * (`SpecEditIsland.tsx:246`): a `1px dashed rgba(0,0,0,0.60)` box, radius 4,
 * padding 64; inside, a centred column, gap 8 — the `h3` `Get Started`, the
 * `body1` line with `pb 24`, and a second `Add group`.
 */
function ZeroState({ target }: { target: SpecEditorTarget }) {
  return (
    <div data-testid="spec-edit-zero-state" className="pr-8">
      <div className="border-lennar-ink-secondary rounded border border-dashed p-16">
        <div className="flex flex-col items-center gap-2">
          <MuiTypography variant="h3">Get Started</MuiTypography>
          <MuiTypography className="pb-6">
            Create your first group and start adding lines and products
          </MuiTypography>
          <AddSpecGroup target={target} />
        </div>
      </div>
    </div>
  );
}

/**
 * The Editor's `LinePaginationSkeleton` (`Skeletons.tsx:30-60`): the pair, and
 * the next page asked for **at once** when it comes within a viewport of the
 * left pane's bottom — an `IntersectionObserver` rooted at the pane,
 * `rootMargin '100% 0px'` — with none of the details island's 300ms settle.
 */
function EditPaginationSkeleton({ onLoadMore }: { onLoadMore: () => void }) {
  const root = useSpecScrollRegion();
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const target = ref.current;
    if (!target) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) onLoadMore();
      },
      { root, rootMargin: "100% 0px", threshold: 0 },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [root, onLoadMore]);

  return <SpecEditSkeletonPair ref={ref} testId="spec-edit-pagination-skeleton" />;
}

/** The summary as the header pencil's dialogs read it: the parent under its link's names. */
function headerEditFields(summary: SpecDetailsSummary): SpecHeaderEditFields {
  const parent = summary.parentVersion;
  return {
    name: summary.name,
    type: summary.type,
    communityId: summary.communityId,
    notes: summary.notes,
    parent: parent
      ? {
          specificationId: parent.specificationId,
          versionId: parent.versionId,
          name: parent.specificationName,
          versionCode: parent.versionCode,
        }
      : null,
  };
}
